import { IApiService, IQuoteService, ITpaService, IUserService, User, PipelineMetrics, SoldCasesData, EnrollmentsData } from './interfaces/IApiService';
import { seedDemoPipelineIfEmpty, seedDemoSoldCasesIfEmpty, seedDemoEnrollmentsDataIfEmpty, demoUsersStore, seedDemoUsersIfEmpty, demoOrganizationsStore, seedDemoOrganizationsIfEmpty } from '../utils/demo';
import { whiteLabelStore, WhiteLabelConfig } from '../utils/whiteLabel';
import { Organization, NewOrganization } from '../types/organization';

interface DemoQuote {
  submissionId: string;
  companyName: string;
  transperraRep: string;
  ichraEffectiveDate: string;
  pepm: string;
  status: string;
  brokerName: string;
  brokerEmail?: string;
  employerName: string;
  brokerId: string;
  employerId: string;
  agency?: string;
  s3Key: string;
  proposalGenerated?: boolean;
}

interface DemoBroker {
  id: string;
  name: string;
  agency?: string;
  email?: string;
  phoneNumber?: string;
}

interface DemoTpa {
  id: string;
  name: string;
  contactEmail: string;
  phone: string;
  address: string;
  status: 'active' | 'inactive';
  clientCount: number;
}

const LS_QUOTES_KEY = 'demo.quotes';
const LS_BROKERS_KEY = 'demo.brokers';

const predefinedDemoQuotes: DemoQuote[] = [
  {
    submissionId: 'demo-quote-1',
    companyName: "Sam's Auto",
    transperraRep: 'Waleed Bahouth',
    ichraEffectiveDate: '2025-10-01',
    pepm: '95',
    status: 'In Progress',
    brokerName: 'ACC',
    brokerEmail: 'waleed.bahouth@kyndly.com',
    employerName: "Sam's Auto",
    brokerId: 'broker_sam',
    employerId: 'emp_sam',
    agency: '-',
    s3Key: 'demo/quotes/1'
  },
  {
    submissionId: 'demo-quote-3',
    companyName: 'Cart & Co',
    transperraRep: 'Waleed Bahouth',
    ichraEffectiveDate: '2025-10-01',
    pepm: '70',
    status: 'Sold Case',
    brokerName: 'Cart',
    brokerEmail: 'waleed.bahouth@kyndly.com',
    employerName: 'Cart & Co',
    brokerId: 'broker_cart',
    employerId: 'emp_cart',
    agency: '-',
    s3Key: 'demo/sold/3'
  },
  {
    submissionId: 'demo-quote-4',
    companyName: "Salvage Co",
    transperraRep: 'Erik Anderson',
    ichraEffectiveDate: '2025-10-01',
    pepm: '70',
    status: 'In Progress',
    brokerName: 'demo-broker',
    brokerEmail: 'erik.anderson@kyndly.com',
    employerName: "Salvage Co",
    brokerId: 'broker_salvage',
    employerId: 'emp_salvage',
    agency: '-',
    s3Key: 'demo/quotes/4'
  }
];

const predefinedDemoBrokers: DemoBroker[] = [
  { id: 'demo-broker', name: 'Demo Broker', agency: 'Demo Agency' },
  { id: 'broker_sam', name: 'ACC', agency: 'ACC Agency', email: 'waleed.bahouth@kyndly.com' },
  { id: 'broker_cart', name: 'Cart', agency: 'Cart Agency', email: 'waleed.bahouth@kyndly.com' },
  { id: 'broker_salvage', name: 'demo-broker', agency: 'Demo Agency', email: 'erik.anderson@kyndly.com' }
];

const mockTpas: DemoTpa[] = [
  {
    id: 'TPA001',
    name: 'Premium Benefits Admin',
    contactEmail: 'contact@premiumbenefits.com',
    phone: '(555) 123-4567',
    address: '123 Business Park Dr, Suite 100, Chicago, IL 60601',
    status: 'active',
    clientCount: 45
  },
  {
    id: 'TPA002',
    name: 'Corporate Health Partners',
    contactEmail: 'info@corporatehealth.com',
    phone: '(555) 987-6543',
    address: '456 Healthcare Blvd, Dallas, TX 75201',
    status: 'active',
    clientCount: 32
  },
  {
    id: 'TPA003',
    name: 'Employee Wellness Solutions',
    contactEmail: 'support@empwellness.com',
    phone: '(555) 456-7890',
    address: '789 Wellness Way, San Francisco, CA 94105',
    status: 'inactive',
    clientCount: 18
  }
];

class MockQuoteService implements IQuoteService {
  private initializeDemoData(): void {
    if (this.getStoredQuotes().length === 0) {
      this.storeQuotes(predefinedDemoQuotes);
    }
    if (this.getStoredBrokers().length === 0) {
      this.storeBrokers(predefinedDemoBrokers);
    }
  }

  private getStoredQuotes(): DemoQuote[] {
    try {
      const raw = localStorage.getItem(LS_QUOTES_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private storeQuotes(quotes: DemoQuote[]): void {
    localStorage.setItem(LS_QUOTES_KEY, JSON.stringify(quotes));
  }

  private getStoredBrokers(): DemoBroker[] {
    try {
      const raw = localStorage.getItem(LS_BROKERS_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private storeBrokers(brokers: DemoBroker[]): void {
    localStorage.setItem(LS_BROKERS_KEY, JSON.stringify(brokers));
  }

  async getQuotes(tpaId?: string): Promise<DemoQuote[]> {
    console.log('MockQuoteService: Getting quotes', tpaId ? `for TPA ${tpaId}` : '');

    this.initializeDemoData();
    await this.simulateDelay();

    const quotes = this.getStoredQuotes();

    if (tpaId) {
      return quotes.filter(quote => quote.brokerId === tpaId || quote.employerId === tpaId);
    }

    return quotes;
  }

  async getQuoteById(id: string, brokerId?: string, employerId?: string): Promise<DemoQuote | null> {
    console.log('MockQuoteService: Getting quote by ID:', id, 'brokerId:', brokerId, 'employerId:', employerId);

    this.initializeDemoData();
    await this.simulateDelay();

    const quotes = this.getStoredQuotes();
    const quote = quotes.find(q => q.submissionId === id);
    return quote || null;
  }

  async submitQuote(quoteData: any): Promise<DemoQuote> {
    console.log('MockQuoteService: Submitting quote:', quoteData);

    this.initializeDemoData();
    await this.simulateDelay();

    const brokers = this.getStoredBrokers();
    const broker = brokers.find(b => b.id === quoteData.brokerId);
    const brokerName = broker?.name || quoteData.brokerId;

    const submissionId = 'submission-'.concat(String(Date.now()));

    const newQuote: DemoQuote = {
      submissionId,
      companyName: quoteData.companyName || 'New Company',
      transperraRep: quoteData.transperraRep || 'Demo Rep',
      ichraEffectiveDate: quoteData.ichraEffectiveDate || new Date().toISOString().split('T')[0],
      pepm: quoteData.pepm || '70',
      status: 'In Progress',
      brokerName: brokerName,
      brokerEmail: broker?.email,
      employerName: quoteData.employerName || quoteData.companyName || 'New Company',
      brokerId: quoteData.brokerId || 'demo-broker',
      employerId: quoteData.employerId || `emp_${Date.now()}`,
      agency: broker?.agency || '-',
      s3Key: `submissions/demo/${quoteData.brokerId}/${quoteData.employerId}/${submissionId}/submission-data.json`,
      proposalGenerated: quoteData.companyName === 'Legacy Metals' ? false : undefined
    };

    const quotes = this.getStoredQuotes();
    quotes.unshift(newQuote);
    this.storeQuotes(quotes);

    return newQuote;
  }

  async updateQuoteStatus(id: string, status: string, brokerId: string, employerId: string): Promise<any> {
    console.log('MockQuoteService: Updating quote status:', id, 'to', status);

    this.initializeDemoData();
    await this.simulateDelay();

    const quotes = this.getStoredQuotes();
    const updated = quotes.map(q =>
      q.submissionId === id ? { ...q, status } : q
    );
    this.storeQuotes(updated);

    return { success: true, submissionId: id, status };
  }

  private async simulateDelay(ms: number = 500): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

class MockTpaService implements ITpaService {
  async getTpaData(): Promise<DemoTpa[]> {
    console.log('MockTpaService: Getting TPA data');

    await this.simulateDelay();

    return [...mockTpas];
  }

  async getTpaById(id: string): Promise<DemoTpa | null> {
    console.log('MockTpaService: Getting TPA by ID:', id);

    await this.simulateDelay();

    const tpa = mockTpas.find(t => t.id === id);
    return tpa || null;
  }

  private async simulateDelay(ms: number = 300): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

class MockUserService implements IUserService {
  private initializeDemoData(): void {
    seedDemoUsersIfEmpty();
  }

  async getUsers(tpaId?: string): Promise<User[]> {
    console.log('MockUserService: Getting users', tpaId ? `for TPA ${tpaId}` : '');

    this.initializeDemoData();
    await this.simulateDelay();

    if (tpaId) {
      return demoUsersStore.findByTpaId(tpaId);
    }

    return demoUsersStore.readAll();
  }

  async createUser(userData: any): Promise<User> {
    console.log('MockUserService: Creating user:', userData);

    this.initializeDemoData();
    await this.simulateDelay(400);

    const newUser: User = {
      username: userData.username || `user_${Date.now()}`,
      email: userData.email,
      name: userData.name,
      role: userData.role || 'tpa_user',
      tpaId: userData.tpaId,
      tpaName: userData.tpaName,
      brokerId: userData.brokerId,
      brokerName: userData.brokerName,
      agency: userData.agency,
      employerId: userData.employerId,
      employerName: userData.employerName,
      status: 'CONFIRMED',
      enabled: true,
      phoneNumber: userData.phoneNumber
    };

    demoUsersStore.add(newUser);
    return newUser;
  }

  async updateUser(username: string, userData: any): Promise<User> {
    console.log('MockUserService: Updating user:', username, userData);

    this.initializeDemoData();
    await this.simulateDelay(400);

    demoUsersStore.update(username, userData);
    const updatedUser = demoUsersStore.findByUsername(username);

    if (!updatedUser) {
      throw new Error(`User ${username} not found`);
    }

    return updatedUser;
  }

  async deleteUser(username: string): Promise<void> {
    console.log('MockUserService: Deleting user:', username);

    this.initializeDemoData();
    await this.simulateDelay(400);

    demoUsersStore.remove(username);
  }

  async enableUser(username: string): Promise<void> {
    console.log('MockUserService: Enabling user:', username);

    this.initializeDemoData();
    await this.simulateDelay(300);

    demoUsersStore.update(username, { enabled: true, status: 'CONFIRMED' });
  }

  async disableUser(username: string): Promise<void> {
    console.log('MockUserService: Disabling user:', username);

    this.initializeDemoData();
    await this.simulateDelay(300);

    demoUsersStore.update(username, { enabled: false, status: 'DISABLED' });
  }

  private async simulateDelay(ms: number = 350): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

export class MockApiService implements IApiService {
  public Quote: IQuoteService;
  public Tpa: ITpaService;
  public Users: IUserService;

  constructor() {
    this.Quote = new MockQuoteService();
    this.Tpa = new MockTpaService();
    this.Users = new MockUserService();
  }

  async checkApiHealth(): Promise<boolean> {
    console.log('MockApiService: Health check - always healthy!');
    return true;
  }

  async getQuoteById(id: string, brokerId?: string, employerId?: string): Promise<any> {
    return this.Quote.getQuoteById(id, brokerId, employerId);
  }

  async submitImplementation(id: string, implementationData: any): Promise<any> {
    console.log('MockApiService: Submitting implementation for quote:', id, implementationData);

    await new Promise(resolve => setTimeout(resolve, 800));

    return {
      success: true,
      submissionId: id,
      implementationId: `impl-${Date.now()}`,
      status: 'submitted',
      message: 'Implementation submitted successfully'
    };
  }

  async saveImplementationDraft(id: string, draftData: any): Promise<any> {
    console.log('MockApiService: Saving implementation draft for quote:', id);

    const draftKey = `implementation_draft_${id}`;
    localStorage.setItem(draftKey, JSON.stringify({
      ...draftData,
      lastSaved: new Date().toISOString(),
      quoteId: id
    }));

    await new Promise(resolve => setTimeout(resolve, 300));

    return {
      success: true,
      draftId: `draft-${id}-${Date.now()}`,
      lastSaved: new Date().toISOString()
    };
  }

  async getDashboardPipeline(): Promise<PipelineMetrics> {
    console.log('MockApiService: Getting dashboard pipeline metrics');
    await new Promise(resolve => setTimeout(resolve, 300));
    return seedDemoPipelineIfEmpty();
  }

  async getSoldCases(): Promise<SoldCasesData> {
    console.log('MockApiService: Getting sold cases data');
    await new Promise(resolve => setTimeout(resolve, 300));
    return seedDemoSoldCasesIfEmpty();
  }

  async getEnrollments(): Promise<EnrollmentsData> {
    console.log('MockApiService: Getting enrollments data');
    await new Promise(resolve => setTimeout(resolve, 300));
    return seedDemoEnrollmentsDataIfEmpty();
  }

  // Organization management methods
  async getOrganizations(): Promise<Organization[]> {
    console.log('MockApiService: Getting organizations');
    seedDemoOrganizationsIfEmpty();
    await new Promise(resolve => setTimeout(resolve, 300));
    return demoOrganizationsStore.readAll();
  }

  async getOrganizationById(id: string): Promise<Organization> {
    console.log('MockApiService: Getting organization by ID:', id);
    seedDemoOrganizationsIfEmpty();
    await new Promise(resolve => setTimeout(resolve, 300));

    const org = demoOrganizationsStore.readAll().find(o => o.id === id);
    if (!org) {
      throw new Error('Organization not found');
    }
    return org;
  }

  async createOrganization(org: NewOrganization): Promise<Organization> {
    console.log('MockApiService: Creating organization:', org);
    seedDemoOrganizationsIfEmpty();
    await new Promise(resolve => setTimeout(resolve, 500));

    return demoOrganizationsStore.create(org);
  }

  async updateOrganization(id: string, updates: Partial<Organization>): Promise<Organization> {
    console.log('MockApiService: Updating organization:', id, updates);
    seedDemoOrganizationsIfEmpty();
    await new Promise(resolve => setTimeout(resolve, 500));

    return demoOrganizationsStore.update(id, updates);
  }

  async deleteOrganization(id: string): Promise<void> {
    console.log('MockApiService: Deleting organization:', id);
    seedDemoOrganizationsIfEmpty();
    await new Promise(resolve => setTimeout(resolve, 500));

    demoOrganizationsStore.delete(id);
  }

  // White-label methods for demo mode
  async getWhiteLabelConfig(): Promise<WhiteLabelConfig> {
    console.log('MockApiService: Getting white-label config');
    await new Promise(resolve => setTimeout(resolve, 100)); // Simulate API delay
    return whiteLabelStore.get();
  }

  async saveWhiteLabelConfig(config: WhiteLabelConfig): Promise<WhiteLabelConfig> {
    console.log('MockApiService: Saving white-label config:', config);
    await new Promise(resolve => setTimeout(resolve, 200)); // Simulate API delay
    whiteLabelStore.save(config);
    return whiteLabelStore.get();
  }

  async resetWhiteLabelConfig(): Promise<WhiteLabelConfig> {
    console.log('MockApiService: Resetting white-label config');
    await new Promise(resolve => setTimeout(resolve, 100));
    whiteLabelStore.reset();
    return whiteLabelStore.get();
  }
}